/* Vesper Cocktails: Build Your Bundle (Main.dc.html).
   Builder state + summary + one-shot add to cart. Tier values and the discount collection
   come from the VC Bundle hero section ([data-vc-bundle-config]); Shopify applies the real
   saving through automatic discounts, this page only previews it.
   Kits already in the cart (read from /cart.js on load and after every cart:update) count
   toward the tier shown, because checkout applies the discount to cart + bundle together.
   Line items, subtotal and total describe the bundle being added: the saving row shows what
   this bundle adds to the order's saving.
   Non-shop currency: tier amounts are shop-currency settings, so they are scaled by
   Shopify.currency.rate and rounded to cents for display.
   Cart refresh mirrors vc-site.js: header count, then a cart:update / CartAddEvent carrying
   the cart-items sections so Horizon's drawer re-renders, then the drawer opens. */

const KEY = 'vc-bundle-v1';
const DEFAULT_CONFIG = { collection: 'infusion-kits', tiers: [{ qty: 3, off: 990 }, { qty: 6, off: 2975 }, { qty: 9, off: 5364 }] };

let Events = null;
import('@theme/events').then((m) => { Events = m; }).catch(() => {});

const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function formatMoney(cents, format) {
  const fmt = String(format || '${{amount}}');
  const re = /\{\{\s*(\w+)\s*\}\}/;
  const delim = (n, precision, thousands = ',', decimal = '.') => {
    const parts = (Math.abs(n) / 100).toFixed(precision).split('.');
    return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
  };
  const key = (fmt.match(re) || [])[1] || 'amount';
  let v;
  switch (key) {
    case 'amount_no_decimals': v = delim(cents, 0); break;
    case 'amount_with_comma_separator': v = delim(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': v = delim(cents, 0, '.', ','); break;
    case 'amount_with_apostrophe_separator': v = delim(cents, 2, "'", '.'); break;
    case 'amount_with_space_separator': v = delim(cents, 2, ' ', ','); break;
    case 'amount_with_period_and_space_separator': v = delim(cents, 2, ' ', '.'); break;
    default: v = delim(cents, 2);
  }
  return fmt.replace(re, v);
}

/* The page's money filter output for 1234.56 tells us the presentment currency's format
   (shop.money_format is the shop currency's). */
function deriveFormat(sample, fallback) {
  if (!sample) return fallback;
  const s = String(sample).replace(/[  ]/g, ' ');
  const pats = [
    ['1,234.56', 'amount'], ['1.234,56', 'amount_with_comma_separator'], ['1 234,56', 'amount_with_space_separator'],
    ['1 234.56', 'amount_with_period_and_space_separator'], ["1'234.56", 'amount_with_apostrophe_separator'],
    ['1234.56', 'amount'], ['1234,56', 'amount_with_comma_separator'],
    ['1,235', 'amount_no_decimals'], ['1.235', 'amount_no_decimals_with_comma_separator'], ['1235', 'amount_no_decimals'],
  ];
  for (let i = 0; i < pats.length; i += 1) {
    if (s.indexOf(pats[i][0]) !== -1) return s.replace(pats[i][0], '{{' + pats[i][1] + '}}');
  }
  return fallback;
}

const fill = (str, map) => String(str || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));

function readJSON(el) {
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch (_) { return null; }
}

function storeGet() {
  try { const raw = window.localStorage.getItem(KEY); return raw ? JSON.parse(raw) : {}; } catch (_) { return {}; }
}
function storeSet(qty) {
  try { window.localStorage.setItem(KEY, JSON.stringify(qty)); } catch (_) { /* private mode etc. */ }
}

const setDisabled = (el, on) => { if (el) el.setAttribute('aria-disabled', on ? 'true' : 'false'); };
const isDisabled = (el) => !el || el.disabled || el.getAttribute('aria-disabled') === 'true';

class VCBundle {
  constructor(root) {
    this.root = root;
    this.data = readJSON(root.querySelector('[data-vc-bundle-data]')) || { kits: [], copy: {} };
    this.copy = this.data.copy || {};
    this.max = Math.max(1, Number(this.data.maxQty) || 24);
    this.moneyFormat = deriveFormat(this.data.moneySample, this.data.moneyFormat);
    const cur = window.Shopify && window.Shopify.currency;
    this.rate = cur && cur.active && this.data.shopCurrency && cur.active !== this.data.shopCurrency ? (Number(cur.rate) || 1) : 1;
    this.kits = new Map();
    (this.data.kits || []).forEach((k) => { this.kits.set(String(k.id), k); });
    this.els = {
      count: root.querySelector('[data-vc-bundle-count]'),
      meter: root.querySelector('[data-vc-bundle-meter]'),
      fill: root.querySelector('[data-vc-bundle-fill]'),
      marks: root.querySelector('[data-vc-bundle-marks]'),
      msg: root.querySelector('[data-vc-bundle-msg]'),
      info: root.querySelector('[data-vc-bundle-info]'),
      lines: root.querySelector('[data-vc-bundle-lines]'),
      empty: root.querySelector('[data-vc-bundle-empty]'),
      subtotal: root.querySelector('[data-vc-bundle-subtotal]'),
      savingName: root.querySelector('[data-vc-bundle-saving-name]'),
      saving: root.querySelector('[data-vc-bundle-saving]'),
      total: root.querySelector('[data-vc-bundle-total]'),
      cta: root.querySelector('[data-vc-bundle-cta]'),
      status: root.querySelector('[data-vc-bundle-status]'),
      error: root.querySelector('[data-vc-bundle-error]'),
      clear: root.querySelector('[data-vc-bundle-clear]'),
      heading: root.querySelector('[data-vc-bundle-heading]'),
    };
    this.busy = false;
    this.lastTotal = null;
    this.lastSt = null;
    this.cart = { eligible: 0, eligibleSubtotal: 0 };
    this.reconfig();

    const saved = storeGet();
    this.qty = {};
    Object.keys(saved || {}).forEach((id) => {
      const k = this.kits.get(id);
      const q = Math.round(Number(saved[id]));
      if (k && k.available && q > 0) this.qty[id] = Math.min(q, this.max);
    });

    this.onClick = this.onClick.bind(this);
    root.addEventListener('click', this.onClick);
    this.panel = root.querySelector('.vc-bundle__panel');
    this.main = root.querySelector('.vc-bundle__main');
    this.grid = root.querySelector('.vc-bundle__grid');
    this.bar = root.querySelector('[data-vc-bundle-bar]');
    this.barEls = this.bar ? {
      filled: this.bar.querySelector('[data-bar-filled]'),
      total: this.bar.querySelector('[data-bar-total]'),
      save: this.bar.querySelector('[data-bar-save]'),
      count: this.bar.querySelector('[data-bar-count]'),
      msg: this.bar.querySelector('[data-bar-msg]'),
      btn: this.bar.querySelector('[data-bar-btn]'),
    } : null;
    this.seen = { panel: false, grid: false };
    this.stacked = false;
    this.updateLayout = this.updateLayout.bind(this);
    window.addEventListener('resize', this.updateLayout, { passive: true });
    if (this.panel && 'ResizeObserver' in window) {
      this.ro = new ResizeObserver(this.updateLayout);
      this.ro.observe(this.panel);
      if (this.bar) this.ro.observe(this.bar);
    }
    if (this.bar && 'IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.target === this.panel) this.seen.panel = en.isIntersecting;
          if (en.target === this.grid) this.seen.grid = en.isIntersecting;
        });
        this.updateBar();
      }, { rootMargin: '0px 0px -40px 0px' });
      if (this.panel) this.io.observe(this.panel);
      if (this.grid) this.io.observe(this.grid);
    }
    this.onCartUpdate = () => { this.syncCart(); };
    document.addEventListener('cart:update', this.onCartUpdate);
    this.render();
    this.updateLayout();
    this.syncCart();
  }

  /* Kits from this page's list that are already in the cart and count toward bundle pricing. */
  async syncCart() {
    const ticket = (this.syncTicket = (this.syncTicket || 0) + 1);
    try {
      const res = await fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      if (ticket !== this.syncTicket) return;
      let e = 0; let es = 0;
      (cart.items || []).forEach((it) => {
        const k = this.kits.get(String(it.variant_id));
        if (k && k.eligible) { e += it.quantity; es += it.quantity * k.price; }
      });
      this.cart = { eligible: e, eligibleSubtotal: es };
      this.render();
    } catch (_) { /* keep the last known cart */ }
  }

  updateLayout() {
    // Stacked = the summary has wrapped under the grid (flex-wrap), not a fixed breakpoint.
    // Compare left edges: the sticky panel's offsetTop moves while it is stuck, its left does not.
    if (this.panel && this.main) {
      const stacked = this.panel.offsetLeft < this.main.offsetLeft + this.main.offsetWidth;
      if (stacked !== this.stacked) { this.stacked = stacked; this.root.classList.toggle('is-stacked', stacked); }
    }
    if (this.bar && this.stacked) {
      this.root.style.setProperty('--vc-bundle-bar-space', (this.bar.offsetHeight + 36) + 'px');
    }
    if (this.lastSt && !this.busy) this.setCta(this.lastSt);
    this.updateBar();
  }

  updateBar() {
    if (!this.bar) return;
    const show = this.stacked && this.seen.grid && !this.seen.panel;
    if (show === this.bar.classList.contains('is-visible')) return;
    this.bar.classList.toggle('is-visible', show);
    this.bar.setAttribute('aria-hidden', String(!show));
    if (show) this.bar.removeAttribute('inert'); else this.bar.setAttribute('inert', '');
  }

  renderBar(st, msg) {
    const b = this.barEls;
    if (!b) return;
    const c = this.copy;
    const empty = st.count === 0;
    this.bar.classList.toggle('is-empty', empty);
    b.filled.hidden = empty;
    b.btn.hidden = empty;
    b.count.hidden = empty;
    if (empty) {
      const first = this.tiers[0];
      b.msg.textContent = st.cartEligible > 0
        ? msg
        : fill(c.barEmpty || c.emptyMsg, { n: first.qty, kits: this.kitWord(first.qty), amount: this.money(first.off) });
      return;
    }
    b.total.textContent = this.money(st.total);
    b.save.textContent = st.off > 0 ? fill(c.barSave, { amount: this.money(st.off) }) : '';
    b.count.textContent = fill(st.count === 1 && c.countOne ? c.countOne : c.count, { n: st.count });
    b.msg.textContent = msg;
  }

  goToSummary() {
    if (!this.panel) return;
    const hh = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--vc-header-height')) || 105;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = this.panel.getBoundingClientRect().top + window.scrollY - hh - 16;
    window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
    if (this.els.heading) this.els.heading.focus({ preventScroll: true });
  }

  reconfig() {
    const cfg = readJSON(document.querySelector('[data-vc-bundle-config]')) || {};
    const tiers = Array.isArray(cfg.tiers) && cfg.tiers.length ? cfg.tiers : DEFAULT_CONFIG.tiers;
    this.tiers = tiers.map((t) => ({ qty: Math.round(Number(t.qty)), off: Math.round((Number(t.off) || 0) * this.rate) }))
      .filter((t) => t.qty > 0).sort((a, b) => a.qty - b.qty);
    if (!this.tiers.length) this.tiers = DEFAULT_CONFIG.tiers.map((t) => ({ qty: t.qty, off: Math.round(t.off * this.rate) }));
    this.collection = cfg.collection || DEFAULT_CONFIG.collection;
    this.kits.forEach((k) => { k.eligible = Array.isArray(k.cols) && k.cols.indexOf(this.collection) !== -1; });
    this.buildMarks();
    this.updateHeroAmounts();
  }

  money(cents) { return formatMoney(cents, this.moneyFormat); }
  kitWord(n) { return n === 1 ? (this.copy.kitOne || 'bundle kit') : (this.copy.kitMany || 'bundle kits'); }

  /* The hero cards are rendered in Liquid with shop-currency savings; rescale them here. */
  updateHeroAmounts() {
    if (this.rate === 1) return;
    let base = null;
    this.kits.forEach((k) => { if (!base && k.eligible && k.available) base = k; });
    if (!base) return;
    document.querySelectorAll('[data-vc-bundle-tier]').forEach((el) => {
      const t = this.tiers.find((x) => x.qty === Number(el.dataset.qty));
      if (!t) return;
      const total = base.price * t.qty - t.off;
      const set = (sel, v) => { const n = el.querySelector(sel); if (n) n.textContent = v; };
      set('[data-tier-save]', this.money(t.off));
      set('[data-tier-total]', this.money(total));
      set('[data-tier-per]', this.money(Math.round(total / t.qty)));
    });
  }

  buildMarks() {
    const top = this.tiers[this.tiers.length - 1].qty;
    if (this.els.meter) {
      this.els.meter.setAttribute('aria-valuemax', String(top));
      this.els.meter.querySelectorAll('.vc-bundle__tick').forEach((el) => el.remove());
      this.tiers.slice(0, -1).forEach((t) => {
        const tick = document.createElement('div');
        tick.className = 'vc-bundle__tick';
        tick.style.left = ((t.qty / top) * 100).toFixed(2) + '%';
        this.els.meter.appendChild(tick);
      });
    }
    if (this.els.marks) {
      this.els.marks.textContent = '';
      this.tiers.forEach((t) => {
        const span = document.createElement('span');
        span.className = 'vc-bundle__mark';
        span.style.right = (100 - (t.qty / top) * 100).toFixed(2) + '%';
        span.textContent = t.qty + ' · ' + this.money(t.off);
        this.els.marks.appendChild(span);
      });
    }
  }

  setQty(id, n) {
    const k = this.kits.get(String(id));
    if (!k || !k.available) return;
    const q = Math.max(0, Math.min(this.max, Math.round(n)));
    if (q > 0) this.qty[id] = q; else delete this.qty[id];
    storeSet(this.qty);
    this.hideNotices();
    this.render();
  }

  hideNotices() {
    if (this.els.status) this.els.status.hidden = true;
    if (this.els.error) this.els.error.hidden = true;
  }

  onClick(e) {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    if (t.closest('[data-bar-btn]')) { this.goToSummary(); return; }
    const add = t.closest('[data-vc-bundle-add]');
    if (add) {
      if (this.busy || isDisabled(add)) return;
      const id = add.dataset.vcBundleAdd;
      this.setQty(id, (this.qty[id] || 0) + 1);
      return;
    }
    const step = t.closest('[data-step]');
    if (step) {
      if (this.busy || isDisabled(step)) return;
      const li = step.closest('[data-id]');
      if (li) this.setQty(li.dataset.id, (this.qty[li.dataset.id] || 0) + Number(step.dataset.step));
      return;
    }
    const clear = t.closest('[data-vc-bundle-clear]');
    if (clear) {
      if (this.busy || isDisabled(clear)) return;
      this.qty = {};
      storeSet(this.qty);
      this.hideNotices();
      this.render();
      if (this.els.heading) this.els.heading.focus({ preventScroll: true });
      return;
    }
    if (t.closest('[data-vc-bundle-cta]')) this.submit();
  }

  tierFor(n) {
    let idx = -1;
    this.tiers.forEach((t, i) => { if (n >= t.qty) idx = i; });
    return idx;
  }

  compute() {
    let count = 0; let eligible = 0; let subtotal = 0; let eligibleSubtotal = 0;
    const lines = [];
    this.kits.forEach((k, id) => {
      const q = this.qty[id] || 0;
      if (!q) return;
      count += q;
      subtotal += q * k.price;
      if (k.eligible) { eligible += q; eligibleSubtotal += q * k.price; }
      lines.push({ id, k, q });
    });
    const cartEligible = this.cart.eligible;
    const effE = eligible + cartEligible;
    // Checkout applies one tier to cart + bundle; the bundle adds the difference.
    const tierIdx = this.tierFor(effE);
    const orderOff = tierIdx >= 0 ? Math.min(this.tiers[tierIdx].off, eligibleSubtotal + this.cart.eligibleSubtotal) : 0;
    const cartIdx = this.tierFor(cartEligible);
    const cartOff = cartIdx >= 0 ? Math.min(this.tiers[cartIdx].off, this.cart.eligibleSubtotal) : 0;
    const off = Math.max(0, orderOff - cartOff);
    return { count, eligible, cartEligible, effE, subtotal, off, orderOff, total: subtotal - off, tierIdx, lines };
  }

  render() {
    const c = this.copy;
    const st = this.compute();
    this.lastSt = st;
    const top = this.tiers[this.tiers.length - 1];
    const tierPick = (arr, i) => (Array.isArray(arr) && arr.length ? arr[Math.min(i, arr.length - 1)] : '');
    const maxText = fill(c.maxLabel || 'Max [n] Per Kit', { n: this.max });
    this.root.classList.toggle('is-busy', this.busy);

    // Cards
    this.root.querySelectorAll('[data-vc-bundle-kit]').forEach((el) => {
      const id = el.dataset.vcBundleKit;
      const k = this.kits.get(id);
      if (!k) return;
      const q = this.qty[id] || 0;
      const flag = el.querySelector('[data-flag]');
      if (flag) { flag.hidden = q === 0; flag.textContent = q ? fill(c.flag, { n: q }) : ''; }
      const note = el.querySelector('[data-note]');
      if (note) note.hidden = !!k.eligible;
      const btn = el.querySelector('[data-vc-bundle-add]');
      if (!btn || !k.available) return;
      const capped = q >= this.max;
      // aria-disabled, not disabled: a focused button keeps focus when it hits the cap.
      btn.textContent = capped ? maxText : (q > 0 ? c.addMore : c.add);
      setDisabled(btn, capped || this.busy);
      btn.setAttribute('aria-label', capped
        ? k.title + ': ' + q + ' in bundle, ' + maxText.toLowerCase()
        : (q > 0 ? 'Add another ' : 'Add ') + k.title + ' to bundle' + (q > 0 ? ', ' + q + ' in bundle' : ''));
    });

    // Count + meter (the meter counts bundle kits in the cart too)
    if (this.els.count) this.els.count.textContent = fill(st.count === 1 && c.countOne ? c.countOne : c.count, { n: st.count });
    if (this.els.fill) this.els.fill.style.width = ((Math.min(st.effE, top.qty) / top.qty) * 100).toFixed(2) + '%';
    if (this.els.meter) {
      this.els.meter.setAttribute('aria-valuenow', String(Math.min(st.effE, top.qty)));
      this.els.meter.setAttribute('aria-valuetext', st.effE + ' of ' + top.qty + ' ' + this.kitWord(top.qty));
    }

    // Next-tier message: always in bundle kits, counting the cart
    const first = this.tiers[0];
    const next = this.tiers[st.tierIdx + 1];
    let head; let msg;
    if (st.count === 0 && st.cartEligible === 0) {
      head = c.emptyHead;
      msg = fill(c.emptyMsg, { n: first.qty, kits: this.kitWord(first.qty), amount: this.money(first.off) });
    } else if (st.tierIdx < 0) {
      const n = first.qty - st.effE;
      head = c.belowHead;
      msg = fill(c.nextMsg, { n, kits: this.kitWord(n), amount: this.money(first.off) });
    } else if (next) {
      const n = next.qty - st.effE;
      head = tierPick(c.heads, st.tierIdx);
      msg = fill(c.nextMsg, { n, kits: this.kitWord(n), amount: this.money(next.off) });
    } else {
      head = tierPick(c.heads, st.tierIdx);
      msg = fill(c.bestMsg, { amount: this.money(st.orderOff) });
    }
    if (this.els.msg) {
      const strong = this.els.msg.querySelector('strong');
      const span = this.els.msg.querySelector('span');
      if (strong && strong.textContent !== head) strong.textContent = head;
      if (span && span.textContent !== msg) span.textContent = msg;
    }
    if (this.els.info) {
      const info = [];
      if (st.count > st.eligible) info.push(fill(c.mixed, { e: st.eligible, n: st.count }));
      if (st.cartEligible > 0) {
        let line = st.cartEligible === 1 ? c.cartOne : fill(c.cartMany, { n: st.cartEligible });
        if (st.orderOff > 0) line += ' ' + fill(c.cartSaving, { amount: this.money(st.orderOff) });
        info.push(line);
      }
      const text = info.join('\n');
      if (this.els.info.textContent !== text) this.els.info.textContent = text;
      this.els.info.hidden = info.length === 0;
    }
    this.renderBar(st, msg);

    // Lines
    this.renderLines(st.lines, maxText);
    if (this.els.lines) this.els.lines.hidden = st.lines.length === 0;
    if (this.els.empty) this.els.empty.hidden = st.lines.length > 0;

    // Totals (the bundle being added)
    if (this.els.subtotal) this.els.subtotal.textContent = this.money(st.subtotal);
    if (this.els.savingName) this.els.savingName.textContent = st.tierIdx >= 0 ? tierPick(c.names, st.tierIdx) : c.noneName;
    if (this.els.saving) this.els.saving.textContent = st.off > 0 ? '−' + this.money(st.off) : this.money(0);
    if (this.els.total && this.lastTotal !== st.total) { this.els.total.textContent = this.money(st.total); this.lastTotal = st.total; }

    // Buttons
    if (!this.busy) this.setCta(st);
    if (this.els.clear) { this.els.clear.disabled = st.count === 0; setDisabled(this.els.clear, this.busy); }

    // Hero tier cards
    const onQty = st.tierIdx >= 0 ? this.tiers[st.tierIdx].qty : -1;
    document.querySelectorAll('[data-vc-bundle-tier]').forEach((el) => {
      const on = Number(el.dataset.qty) === onQty;
      el.classList.toggle('is-on', on);
      const badge = el.querySelector('[data-badge]');
      if (badge) badge.hidden = !on;
    });
  }

  /* Full label when it fits on one line, else the short one, else let it wrap. */
  setCta(st) {
    const cta = this.els.cta;
    if (!cta) return;
    const c = this.copy;
    cta.disabled = st.count === 0;
    const price = st.count === 0 ? '' : ' · ' + this.money(st.total);
    cta.classList.remove('is-wrap');
    cta.textContent = c.cta + price;
    if (cta.clientWidth > 0 && cta.scrollWidth > cta.clientWidth + 1) {
      cta.textContent = (c.ctaShort || c.cta) + price;
      if (cta.scrollWidth > cta.clientWidth + 1) cta.classList.add('is-wrap');
    }
  }

  makeLine(id, k) {
    const li = document.createElement('li');
    li.className = 'vc-bundle__line';
    li.dataset.id = id;
    if (k.img) {
      const img = document.createElement('img');
      img.className = 'vc-bundle__line-img';
      img.src = k.img; img.alt = ''; img.width = 44; img.height = 44; img.loading = 'lazy';
      li.appendChild(img);
    }
    const body = document.createElement('div');
    body.className = 'vc-bundle__line-body';
    const name = document.createElement('div');
    name.className = 'vc-bundle__line-name';
    name.textContent = k.title;
    const sub = document.createElement('div');
    sub.className = 'vc-bundle__line-sub';
    body.append(name, sub);
    const stepper = document.createElement('div');
    stepper.className = 'vc-bundle__stepper';
    stepper.setAttribute('role', 'group');
    stepper.setAttribute('aria-label', 'Quantity of ' + k.title);
    const dec = document.createElement('button');
    dec.type = 'button'; dec.className = 'vc-bundle__step'; dec.dataset.step = '-1';
    dec.textContent = '–';
    const val = document.createElement('span');
    val.className = 'vc-bundle__qty';
    const inc = document.createElement('button');
    inc.type = 'button'; inc.className = 'vc-bundle__step'; inc.dataset.step = '1';
    inc.textContent = '+';
    stepper.append(dec, val, inc);
    li.append(body, stepper);
    return li;
  }

  renderLines(lines, maxText) {
    const list = this.els.lines;
    if (!list) return;
    const c = this.copy;
    const wanted = new Set(lines.map((l) => l.id));
    const active = document.activeElement;
    let lostFocusAt = -1;
    Array.from(list.children).forEach((li, i) => {
      if (!wanted.has(li.dataset.id)) {
        if (li.contains(active)) lostFocusAt = i;
        li.remove();
      }
    });
    lines.forEach((l, i) => {
      let li = list.querySelector('[data-id="' + l.id + '"]');
      if (!li) li = this.makeLine(l.id, l.k);
      if (list.children[i] !== li) list.insertBefore(li, list.children[i] || null);
      const capped = l.q >= this.max;
      const sub = l.q + ' × ' + this.money(l.k.price) + ' · ' + this.money(l.q * l.k.price)
        + (l.k.eligible ? '' : ' · ' + c.fullPrice) + (capped ? ' · ' + maxText : '');
      const subEl = li.querySelector('.vc-bundle__line-sub');
      if (subEl.textContent !== sub) subEl.textContent = sub;
      li.querySelector('.vc-bundle__qty').textContent = String(l.q);
      const dec = li.querySelector('[data-step="-1"]');
      dec.setAttribute('aria-label', (l.q === 1 ? 'Remove ' : 'Remove one ') + l.k.title + (l.q === 1 ? ' from bundle' : ''));
      setDisabled(dec, this.busy);
      const inc = li.querySelector('[data-step="1"]');
      inc.setAttribute('aria-label', capped ? l.k.title + ': ' + maxText.toLowerCase() : 'Add one ' + l.k.title);
      setDisabled(inc, this.busy || capped);
    });
    if (lostFocusAt >= 0) {
      const target = list.children[Math.min(lostFocusAt, list.children.length - 1)];
      const btn = target && target.querySelector('[data-step="-1"]');
      if (btn) btn.focus({ preventScroll: true });
      else if (this.els.heading) this.els.heading.focus({ preventScroll: true });
    }
  }

  async submit() {
    const c = this.copy;
    const st = this.compute();
    const items = st.lines.filter((l) => l.k.available).map((l) => ({ id: Number(l.id), quantity: l.q }));
    if (!items.length || this.busy) return;
    this.busy = true;
    this.hideNotices();
    const cta = this.els.cta;
    cta.setAttribute('aria-busy', 'true');
    cta.disabled = true;
    cta.classList.remove('is-wrap');
    cta.textContent = c.adding;
    this.render();

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    const body = { items };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    try {
      let res;
      try {
        res = await fetch(cartRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
      } catch (_) {
        throw new Error(c.error); // network failure: never show the browser's "Failed to fetch"
      }
      let json = {};
      try { json = await res.json(); } catch (_) { /* non-JSON */ }
      if (!res.ok || json.status) throw new Error(json.description || c.error);

      let cartCount;
      try {
        const cart = await (await fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        cartCount = cart.item_count;
      } catch (_) { /* no-op */ }
      if (typeof cartCount === 'number') {
        document.querySelectorAll('[data-vc-cart-count]').forEach((el) => { el.textContent = String(cartCount); });
      }

      this.qty = {};
      storeSet(this.qty);
      this.busy = false;
      cta.removeAttribute('aria-busy');
      this.render();
      if (this.els.status) { this.els.status.textContent = c.added; this.els.status.hidden = false; }

      // Fires our own syncCart too, so the summary picks up the kits now in the cart.
      const data = { source: 'vc-bundle', itemCount: cartCount, cartCount, sections: json.sections };
      const evt = Events && Events.CartAddEvent
        ? new Events.CartAddEvent({}, 'vc-bundle', data)
        : Object.assign(new Event('cart:update', { bubbles: true }), { detail: { resource: {}, sourceId: 'vc-bundle', data } });
      document.dispatchEvent(evt);
      this.openCart();
    } catch (err) {
      this.busy = false;
      cta.removeAttribute('aria-busy');
      this.render();
      if (this.els.error) {
        this.els.error.textContent = (err && err.message) || c.error;
        this.els.error.hidden = false;
      }
    }
  }

  openCart() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer || typeof drawer.open !== 'function') { window.location.href = cartRoot() + 'cart'; return; }
    setTimeout(() => {
      const dialog = drawer.querySelector('dialog');
      if (dialog && dialog.open) return;
      try { drawer.open(); } catch (_) { window.location.href = cartRoot() + 'cart'; }
    }, 120);
  }

  destroy() {
    this.root.removeEventListener('click', this.onClick);
    window.removeEventListener('resize', this.updateLayout);
    document.removeEventListener('cart:update', this.onCartUpdate);
    if (this.ro) this.ro.disconnect();
    if (this.io) this.io.disconnect();
  }
}

const instances = new Set();
function init() {
  instances.forEach((inst) => { if (!inst.root.isConnected) { inst.destroy(); instances.delete(inst); } });
  document.querySelectorAll('[data-vc-bundle]').forEach((root) => {
    if (root.__vcBundle) { root.__vcBundle.reconfig(); root.__vcBundle.render(); return; }
    root.__vcBundle = new VCBundle(root);
    instances.add(root.__vcBundle);
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:section:unload', () => setTimeout(init, 0));
